import React, { useMemo, useState, useEffect, useCallback } from "react";
import axios from 'axios';
import {
  ProblemRunWithProblem,
  ProjectWithStatistics,
  downloadInputFile,
  checkIfSolutionExists,
  createLabel,
  createScoreDispute,
  deleteLabel,
  fetchProblemRunDisputes,
  deleteDispute,
  API_BASE_URL,
  getAuthHeaders,
} from "../api";
import { HumanLabel, LabelType } from "../types";
import SolutionUploadSection, {
  SolutionStatus,
} from "./SolutionUploadSection";
import DownloadToast from "./DownloadToast";
import { formatDueDateWithTimezone } from "../utils/dateFormatting";
import "./ProjectProblemWorkspace.css";
import OnboardingOverlayNew from "./OnboardingOverlayNew";
import { useTutorial, getTutorialConfig } from "../services/tutorial";
import PlaygroundModal from "./PlaygroundModal";
import MultimediaTaskHelpModal from "./MultimediaTaskHelpModal";

interface ProjectProblemWorkspaceProps {
  projectId: string;
  project: ProjectWithStatistics | null;
  runs: ProblemRunWithProblem[];
  selectedProblemId: string | null;
  selectedRunId: string | null;
  onSelectProblem: (problemId: string) => void;
  onSelectRun: (runId: string) => void;
  onSolutionUploaded?: (problemId: string) => void;
  userId: string | null;
  onRunLabelChange?: (runId: string, label: HumanLabel | null) => void;
  onBadTaskReasonUpdate?: (problemId: string, reason: string) => void;
  isReadOnly?: boolean;
}

interface ProblemGroup {
  id: string;
  name: string;
  prompt: string;
  software?: string;
  mattermost_state?: any;
  runs: ProblemRunWithProblem[];
  runIndices: number[];
  labeledCount: number;
  badTaskReason: string | null;
  isFullyBadTask: boolean;
}

type ProblemGroupInternal = ProblemGroup & {
  badTaskLabelCount: number;
  badTaskReasons: Set<string>;
  hasBadTaskDispute: boolean;
};

const SOLUTION_UPLOAD_NOTE = "SOLUTION_UPLOAD";
const LABEL_PLATFORM_BAD_TASK_PREFIX = "Marked as Bad Task from the Label Platform for reason:";

const extractBadTaskReason = (notes?: string | null): string | null => {
  if (!notes) return null;
  const trimmed = notes.trim();
  if (trimmed === "BAD_TASK") return "";
  if (trimmed.startsWith("BAD_TASK:")) {
    return trimmed.slice("BAD_TASK:".length).trim();
  }
  return null;
};

const getProblemId = (run: ProblemRunWithProblem) =>
  run.problem?.id || run.problem_id;

const ProjectProblemWorkspace: React.FC<ProjectProblemWorkspaceProps> = ({
  projectId,
  project,
  runs,
  selectedProblemId,
  selectedRunId,
  onSelectProblem,
  onSelectRun,
  onSolutionUploaded,
  userId,
  onRunLabelChange,
  onBadTaskReasonUpdate,
  isReadOnly = false,
}) => {
  const tutorial = useTutorial();
  const tutorialConfig = getTutorialConfig(tutorial.state.software);
  const currentTutorialStep = tutorialConfig?.steps.find(
    (step) => step.id === tutorial.state.currentStepId,
  );
  const showProjectTutorialOverlay =
    tutorial.state.isActive && currentTutorialStep?.scope === "project";

  console.log(`[ProjectProblemWorkspace] Tutorial overlay state`, {
    isActive: tutorial.state.isActive,
    currentStepId: tutorial.state.currentStepId,
    currentStepScope: currentTutorialStep?.scope,
    showProjectTutorialOverlay,
    currentTaskIndex: tutorial.state.currentTaskIndex,
  });

  const tutorialCurrentStepId = tutorial.state.currentStepId;
  const tutorialIsActive = tutorial.state.isActive;

  const handleTutorialStepComplete = useCallback(
    (stepId: string) => {
      tutorial.completeStep(stepId);
      if (
        stepId === 'audacity-project-recap' ||
        stepId === 'shotcut-project-recap' ||
        stepId === 'gimp-project-recap' ||
        stepId === 'libreoffice-project-recap' ||
        stepId === 'inkscape-project-recap'
      ) {
        tutorial.nextTask();
      }
    },
    [tutorial],
  );

  const handleTutorialStepPrevious = useCallback(
    (stepId: string) => {
      tutorial.previousStep(stepId);
    },
    [tutorial],
  );

  const handleTutorialComplete = useCallback(() => {
    if (tutorial.state.currentStepId) {
      tutorial.completeStep(tutorial.state.currentStepId);
    }
    tutorial.completeTutorial();
  }, [tutorial]);
  const groupedProblems = useMemo<ProblemGroup[]>(() => {
    const order: string[] = [];
    const map = new Map<string, ProblemGroupInternal>();

    runs.forEach((run, index) => {
      const problemId = getProblemId(run);
      if (!problemId) return;

      if (!map.has(problemId)) {
        order.push(problemId);
        map.set(problemId, {
          id: problemId,
          name: run.problem?.name || `Problem ${order.length + 1}`,
          prompt: run.problem?.spec?.prompt || "No prompt available",
          software: run.problem?.software,
          mattermost_state: (run.problem?.spec as any)?.start_state?.mattermost_state,
          runs: [],
          runIndices: [],
          labeledCount: 0,
          badTaskReason: null,
          isFullyBadTask: false,
          badTaskLabelCount: 0,
          badTaskReasons: new Set<string>(),
          hasBadTaskDispute: false,
        });
      }

      const entry = map.get(problemId)!;
      entry.runs.push(run);
      entry.runIndices.push(index);

      if (userId) {
        const existingLabel = run.human_labels?.find(
          (label) =>
            label.user_id === userId &&
            label.notes !== SOLUTION_UPLOAD_NOTE &&
            !label.deleted_at,
        );
        if (existingLabel) {
          entry.labeledCount += 1;
          const reason = extractBadTaskReason(existingLabel.notes);
          if (reason !== null) {
            entry.badTaskLabelCount += 1;
            entry.badTaskReasons.add(reason);
          }
        }
      }

      const disputeReason = run.label_platform_bad_task_reason ?? null;
      if (disputeReason !== null) {
        entry.badTaskReasons.add(disputeReason);
        entry.hasBadTaskDispute = true;
      }
    });

    return order
      .map((problemId) => {
        const group = map.get(problemId);
        if (!group) return null;

        const { badTaskLabelCount, badTaskReasons, hasBadTaskDispute, ...rest } =
          group;
        const runsCount = rest.runs.length;
        const allRunsBadTaskLabeled =
          runsCount > 0 &&
          badTaskLabelCount === runsCount &&
          rest.labeledCount === runsCount;
        const isFullyBadTask = hasBadTaskDispute || allRunsBadTaskLabeled;
        const reasonCandidates = Array.from(badTaskReasons);
        const reason =
          reasonCandidates.find((candidate) => candidate.length > 0) ??
          (reasonCandidates.length > 0 ? reasonCandidates[0] : null);

        return {
          ...rest,
          badTaskReason: reason,
          isFullyBadTask,
        } as ProblemGroup;
      })
      .filter((group): group is ProblemGroup => Boolean(group));
  }, [runs, userId]);

  const derivedSolutionStatus = useMemo<Record<string, SolutionStatus>>(() => {
    const statusMap: Record<string, SolutionStatus> = {};

    groupedProblems.forEach((problem) => {
      const hasUpload = problem.runs.some((run) =>
        run.human_labels?.some(
          (label) => label.notes === SOLUTION_UPLOAD_NOTE && !label.deleted_at,
        ),
      );

      statusMap[problem.id] = hasUpload ? "uploaded" : "missing";
    });

    return statusMap;
  }, [groupedProblems]);

  const [manualSolutionStatus, setManualSolutionStatus] = useState<
    Record<string, SolutionStatus>
  >({});

  const [downloadProgress, setDownloadProgress] = useState<number | null>(null);
  const [downloadFilename, setDownloadFilename] = useState<string>("");
  const [showBadTaskModal, setShowBadTaskModal] = useState(false);
  const [badTaskReasonInput, setBadTaskReasonInput] = useState<string>("");
  const [submittingBadTask, setSubmittingBadTask] = useState(false);
  const [badTaskError, setBadTaskError] = useState<string | null>(null);
  const [isPlaygroundModalOpen, setIsPlaygroundModalOpen] = useState(false);
  const [showMultimediaHelpModal, setShowMultimediaHelpModal] = useState(false);

  // Fetch actual solution status from API for problems we haven't checked yet
  useEffect(() => {
    if (!groupedProblems.length) {
      return;
    }

    const problemIdsToFetch = groupedProblems
      .map((problem) => problem.id)
      .filter((problemId) => manualSolutionStatus[problemId] === undefined);

    if (problemIdsToFetch.length === 0) {
      return;
    }

    const fetchSolutionStatuses = async () => {
      const statusPromises = problemIdsToFetch.map(
        async (problemId): Promise<{ problemId: string; status: SolutionStatus }> => {
          try {
            const exists = await checkIfSolutionExists(problemId);
            return { problemId, status: exists ? "uploaded" : "missing" };
          } catch (error) {
            console.error(`Failed to check solution for problem ${problemId}:`, error);
            return { problemId, status: "missing" };
          }
        },
      );

      const results = await Promise.all(statusPromises);

      setManualSolutionStatus((prev) => {
        const next = { ...prev };
        let didChange = false;

        for (const { problemId, status } of results) {
          if (next[problemId] !== status) {
            next[problemId] = status;
            didChange = true;
          }
        }

        return didChange ? next : prev;
      });
    };

    fetchSolutionStatuses();
  }, [groupedProblems, manualSolutionStatus]);

  useEffect(() => {
    setManualSolutionStatus((prev) => {
      const validIds = new Set(groupedProblems.map((problem) => problem.id));
      let didMutate = false;
      const next = { ...prev };

      Object.keys(next).forEach((key) => {
        if (!validIds.has(key)) {
          delete next[key];
          didMutate = true;
        }
      });

      return didMutate ? next : prev;
    });
  }, [groupedProblems]);

  const getSolutionStatus = useCallback(
    (problemId: string): SolutionStatus => {
      const manualStatus = manualSolutionStatus[problemId];
      if (manualStatus) {
        return manualStatus;
      }

      return derivedSolutionStatus[problemId] || "missing";
    },
    [derivedSolutionStatus, manualSolutionStatus],
  );

  const activeProblem = useMemo<ProblemGroup | null>(() => {
    if (!groupedProblems.length) {
      return null;
    }

    if (selectedProblemId) {
      const explicit = groupedProblems.find(
        (problem) => problem.id === selectedProblemId,
      );
      if (explicit) {
        return explicit;
      }
    }

    return groupedProblems[0];
  }, [groupedProblems, selectedProblemId]);

  const activeRuns = activeProblem ? activeProblem.runs : [];
  const activeProblemIsBadTask = activeProblem?.isFullyBadTask ?? false;
  const activeProblemBadTaskReason = activeProblem?.badTaskReason ?? "";
  const canMarkBadTask =
    activeRuns.length > 0 && Boolean(userId) && !isReadOnly;

  useEffect(() => {
    console.debug('[ProjectProblemWorkspace] runs update', {
      runCount: runs.length,
      problemCount: groupedProblems.length,
      problemIds: groupedProblems.map((problem) => problem.id),
      isReadOnly,
    });
  }, [runs, groupedProblems, isReadOnly]);

  const handleProblemClick = useCallback(
    (problemId: string) => {
      // Check if this is a multimedia problem with GIMP software
      const problem = groupedProblems.find(p => p.id === problemId);
      const isMultimediaProblem = problem?.mattermost_state !== undefined && problem?.mattermost_state !== null;
      const isGimpProblem = problem?.software?.toLowerCase() === 'gimp';

      // Show help modal if this is a GIMP multimedia problem and user hasn't seen it before
      if (isMultimediaProblem && isGimpProblem) {
        const hasSeenHelp = localStorage.getItem('multimediaTaskHelpSeen');
        if (!hasSeenHelp) {
          setShowMultimediaHelpModal(true);
          localStorage.setItem('multimediaTaskHelpSeen', 'true');
        }
      }

      onSelectProblem(problemId);
    },
    [onSelectProblem, groupedProblems],
  );

  const handleRunClick = useCallback(
    (runId: string) => {
      console.debug('[ProjectProblemWorkspace] Run clicked', {
        runId,
        tutorialActive: tutorialIsActive,
        tutorialStep: tutorialCurrentStepId,
      });

      if (tutorialIsActive) {
        console.debug('[ProjectProblemWorkspace] tutorial active before run navigation', {
          currentStepId: tutorialCurrentStepId,
        });
        tutorial.completeStep(tutorialCurrentStepId);
      }

      onSelectRun(runId);
    },
    [onSelectRun, tutorial, tutorialCurrentStepId, tutorialIsActive],
  );

  const handleDownloadClick = useCallback(async () => {
    if (!activeProblem || activeProblem.runs.length === 0) {
      return;
    }

    const preferredRun = activeProblem.runs[0];
    const problem = preferredRun.problem;

    // Handle different problem spec structures
    let filename: string;
    if (problem?.spec?.start_state) {
      // Check if it's a multimedia problem (object with directory_state)
      if (typeof problem.spec.start_state === 'object' && problem.spec.start_state.directory_state) {
        filename = problem.spec.start_state.directory_state.name || "input file";
      } else if (typeof problem.spec.start_state === 'string') {
        filename = problem.spec.start_state;
      } else {
        filename = "input file";
      }
    } else {
      filename = problem?.spec?.input_file || "input file";
    }

    setDownloadFilename(filename);
    setDownloadProgress(0);

    try {
      await downloadInputFile(preferredRun.id, activeProblem.id, (progress) => {
        setDownloadProgress(progress);
      });

      // Auto-dismiss after 3 seconds when complete
      setTimeout(() => {
        setDownloadProgress(null);
        setDownloadFilename("");
      }, 3000);
    } catch (error) {
      setDownloadProgress(null);
      setDownloadFilename("");
    }
  }, [activeProblem]);

  const handleSolutionUploaded = useCallback(
    async (problemId: string) => {
      // Re-check the actual solution status from the API
      try {
        const exists = await checkIfSolutionExists(problemId);
        setManualSolutionStatus((prev) => ({
          ...prev,
          [problemId]: exists ? "uploaded" : "missing",
        }));
      } catch (error) {
        console.error(`Failed to check solution status for problem ${problemId}:`, error);
        // If the check fails, assume it was deleted
        setManualSolutionStatus((prev) => ({
          ...prev,
          [problemId]: "missing",
        }));
      }

      if (onSolutionUploaded) {
        onSolutionUploaded(problemId);
      }
    },
    [onSolutionUploaded],
  );

  const handleOpenBadTaskModal = useCallback(() => {
    if (!activeProblem || activeRuns.length === 0 || isReadOnly) {
      return;
    }
    setBadTaskReasonInput(activeProblem.badTaskReason || "");
    setBadTaskError(null);
    setShowBadTaskModal(true);
  }, [activeProblem, activeRuns.length, isReadOnly]);

  const handleCloseBadTaskModal = useCallback(() => {
    setShowBadTaskModal(false);
    setBadTaskError(null);
  }, []);

  const handleConfirmBadTask = useCallback(async () => {
    if (isReadOnly) {
      setBadTaskError("Tutorial mode: bad task updates are disabled.");
      return;
    }

    const trimmedReason = badTaskReasonInput.trim();
    if (!trimmedReason) {
      setBadTaskError("Please provide a reason for marking this task as bad.");
      return;
    }

    if (!activeProblem || !userId) {
      setBadTaskError("Unable to mark this problem as a bad task right now.");
      return;
    }

    setSubmittingBadTask(true);
    try {
      const runsToLabel = activeProblem.runs;
      const labels = await Promise.all(
        runsToLabel.map((run) =>
          createLabel(
            run.id,
            userId,
            LabelType.FAILED,
            undefined,
            `BAD_TASK: ${trimmedReason}`,
            projectId,
          ),
        ),
      );

      if (onRunLabelChange) {
        labels.forEach((label, index) => {
          const runId = runsToLabel[index]?.id;
          if (runId) {
            onRunLabelChange(runId, label);
          }
        });
      }

      const disputePromises = runsToLabel.map((run) => {
        const existingReason = run.label_platform_bad_task_reason;
        if (existingReason && existingReason === trimmedReason) {
          return Promise.resolve(null);
        }

        return createScoreDispute({
          problem_run_id: run.id,
          note: `${LABEL_PLATFORM_BAD_TASK_PREFIX} ${trimmedReason}`,
          proposed_score: "bad_task",
        });
      });

      await Promise.all(disputePromises);

      if (onBadTaskReasonUpdate) {
        onBadTaskReasonUpdate(activeProblem.id, trimmedReason);
      }

      setBadTaskReasonInput(trimmedReason);
      setBadTaskError(null);
      setShowBadTaskModal(false);
    } catch (error) {
      console.error("Failed to mark problem as bad task:", error);
      setBadTaskError("Failed to mark this problem as a bad task. Please try again.");
    } finally {
      setSubmittingBadTask(false);
    }
  }, [
    activeProblem,
    badTaskReasonInput,
    isReadOnly,
    onBadTaskReasonUpdate,
    onRunLabelChange,
    projectId,
    userId,
  ]);

  const handleClearBadTask = useCallback(async () => {
    if (!activeProblem || !userId || isReadOnly) {
      return;
    }

    setSubmittingBadTask(true);
    try {
      await Promise.all(
        activeProblem.runs.map(async (run) => {
          const badTaskLabel = run.human_labels?.find(
            (label) =>
              label.user_id === userId &&
              !label.deleted_at &&
              label.notes &&
              label.notes.startsWith("BAD_TASK")
          );

          if (badTaskLabel?.id) {
            await deleteLabel(badTaskLabel.id);
            if (onRunLabelChange) {
              onRunLabelChange(run.id, null);
            }
          }

          const disputes = await fetchProblemRunDisputes(run.id);
          const toDelete = disputes.filter(
            (dispute) =>
              dispute.user_id === userId &&
              dispute.note &&
              dispute.note.startsWith(LABEL_PLATFORM_BAD_TASK_PREFIX)
          );

          for (const dispute of toDelete) {
            await deleteDispute(dispute.id);
          }
        })
      );

      setBadTaskReasonInput("");
      setBadTaskError(null);
      if (onBadTaskReasonUpdate) {
        onBadTaskReasonUpdate(activeProblem.id, "");
      }
    } catch (error) {
      console.error("Failed to clear bad task status:", error);
      setBadTaskError("Failed to clear bad task status. Please try again.");
    } finally {
      setSubmittingBadTask(false);
    }
  }, [activeProblem, isReadOnly, onBadTaskReasonUpdate, onRunLabelChange, userId]);

  const renderProblemPreview = (prompt: string) => {
    if (!prompt) return "No prompt available";
    if (prompt.length <= 140) return prompt;
    return `${prompt.slice(0, 140).trim()}…`;
  };

  const resolveRunStatus = (run: ProblemRunWithProblem) => {
    if (!userId) return "pending";

    const userLabel = run.human_labels?.find(
      (label) =>
        label.user_id === userId &&
        label.notes !== SOLUTION_UPLOAD_NOTE &&
        !label.deleted_at,
    );

    if (!userLabel) return "pending";

    if (userLabel.label === LabelType.SUCCESS) {
      return "labeled-success";
    }

    // Check if it's a bad task label (FAILED with "BAD_TASK" notes)
    if (userLabel.notes && userLabel.notes.startsWith("BAD_TASK")) {
      return "labeled-bad-task";
    }

    return "labeled-failed";
  };

  const handleCloseDownloadToast = useCallback(() => {
    setDownloadProgress(null);
    setDownloadFilename("");
  }, []);

  const activeProblemRef = React.useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (activeProblemRef.current) {
      activeProblemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      activeProblemRef.current.classList.add('highlight-active');
      const timeout = window.setTimeout(() => {
        activeProblemRef.current?.classList.remove('highlight-active');
      }, 1000);
      return () => window.clearTimeout(timeout);
    }
    return undefined;
  }, [activeProblem?.id]);

  return (
    <div className="project-workspace fullscreen">
      {downloadProgress !== null && (
        <DownloadToast
          filename={downloadFilename}
          progress={downloadProgress}
          onClose={handleCloseDownloadToast}
        />
      )}
      <aside className="problem-sidebar fullscreen">
        <div className="sidebar-header">
          <h2 className="sidebar-title">
            {project?.name || "Labeling Project"}
          </h2>
          {project?.due_date && (
            <span className="sidebar-meta">
              Due {formatDueDateWithTimezone(project.due_date)}
            </span>
          )}
        </div>

        <div className="problem-list">
          {groupedProblems.length === 0 && (
            <div className="problem-empty-state">
              <p>No problems have been assigned to this project yet.</p>
            </div>
          )}

          {groupedProblems.map((problem, index) => {
            const status = getSolutionStatus(problem.id);
            const isActive = activeProblem?.id === problem.id;
            const isCompleted =
              problem.labeledCount > 0 &&
              problem.labeledCount === problem.runs.length;

            return (
              <button
                key={problem.id}
                type="button"
                className={`problem-list-item ${isActive ? "active" : ""} ${isCompleted ? "completed" : ""}`.trim()}
                onClick={() => handleProblemClick(problem.id)}
                ref={isActive ? activeProblemRef : undefined}
              >
                <div className="problem-list-header">
                  <span className="problem-index">#{index + 1}</span>
                  <span className="problem-name">{problem.name}</span>
                </div>
                <p className="problem-prompt-preview">
                  {renderProblemPreview(problem.prompt)}
                </p>
                <div className="problem-meta-row">
                  <span className="problem-progress">
                    {problem.labeledCount}/{problem.runs.length} labeled
                  </span>
                  <span
                    className={`solution-indicator ${status} ${problem.isFullyBadTask ? "waived" : ""}`.trim()}
                  >
                    {status === "uploaded"
                      ? "Solution Uploaded"
                      : "Solution Missing"}
                  </span>
                </div>
                <div className="problem-progress-bar-container">
                  <div
                    className="problem-progress-bar"
                    style={{
                      width: `${problem.runs.length > 0 ? (problem.labeledCount / problem.runs.length) * 100 : 0}%`
                    }}
                  />
                </div>
                {problem.isFullyBadTask && (
                  <div className="problem-bad-task-reason" title={problem.badTaskReason || "Marked as Bad Task"}>
                    ⚠ {problem.badTaskReason && problem.badTaskReason.length > 0
                      ? problem.badTaskReason
                      : "Marked as Bad Task"}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </aside>

      <section className="problem-detail fullscreen">
        {activeProblem ? (
          <>
            <div className="problem-detail-header">
              <div>
                <h2>{activeProblem.name}</h2>
                <p className="problem-prompt">
                  {activeProblem.prompt || "No prompt available"}
                </p>
              </div>
              <div className="problem-actions">
                <button
                  type="button"
                  className={`bad-task-btn ${activeProblemIsBadTask ? "update" : ""}`.trim()}
                  onClick={handleOpenBadTaskModal}
                  disabled={!canMarkBadTask || submittingBadTask}
                >
                  ⚠ {activeProblemIsBadTask ? "Update Bad Task Reason" : "Mark as Bad Task"}
                </button>
                <button
                  type="button"
                  className="download-input-btn"
                  onClick={handleDownloadClick}
                  disabled={activeRuns.length === 0}
                >
                  Download Input File
                </button>
                {(() => {
                  console.log('[ProjectProblemWorkspace] activeProblem:', activeProblem);
                  console.log('[ProjectProblemWorkspace] activeProblem.mattermost_state:', activeProblem.mattermost_state);
                  return activeProblem.mattermost_state && (
                    <>
                      <button
                        type="button"
                        className="open-computer-btn"
                        onClick={() => {
                          console.log('[ProjectProblemWorkspace] Open Computer clicked');
                          setIsPlaygroundModalOpen(true);
                        }}
                        disabled={isReadOnly}
                      >
                        <span className="button-icon">💻</span>
                        Open Computer
                      </button>
                    </>
                  );
                })()}
              </div>
            </div>

            {isReadOnly && (
              <div className="tutorial-readonly-banner">
                Tutorial walkthrough – project actions are disabled in this read-only view.
              </div>
            )}

            <SolutionUploadSection
              problemId={activeProblem.id}
              problemName={activeProblem.name}
              projectId={projectId}
              status={getSolutionStatus(activeProblem.id)}
              software={activeProblem.software}
              problemPrompt={activeProblem.prompt}
              onSolutionUploaded={() => handleSolutionUploaded(activeProblem.id)}
              isBadTask={activeProblemIsBadTask}
              isReadOnly={isReadOnly}
            />

            {activeProblemIsBadTask && (
              <div className="bad-task-banner">
                <div className="bad-task-icon">⚠</div>
                <div className="bad-task-details">
                  <div className="bad-task-title">Marked as Bad Task</div>
                  <div className="bad-task-reason">
                    {activeProblemBadTaskReason || "No reason provided."}
                  </div>
                  <div className="bad-task-reason subtle">
                    Solution upload not required for this problem.
                  </div>
                </div>
                <button
                  type="button"
                  className="clear-bad-task-btn"
                  onClick={handleClearBadTask}
                  disabled={submittingBadTask || isReadOnly}
                  title="Remove bad task mark"
                >
                  ×
                </button>
              </div>
            )}

            <div className="run-list-header">
              <h3>Select a Run to Label</h3>
              <span className="run-progress">
                {activeProblem.labeledCount}/{activeProblem.runs.length} labeled
              </span>
            </div>

            <div className="run-list">
              {activeRuns.map((run, idx) => {
                const statusClass = resolveRunStatus(run);
                const isSelected = selectedRunId === run.id;
                const solutionStatus = activeProblem ? getSolutionStatus(activeProblem.id) : "missing";
                const hasSolution = solutionStatus === "uploaded";

                // Determine if runs should be enabled
                let runsEnabled: boolean;
                if (tutorialIsActive) {
                  // During tutorial, control run access based on specific steps
                  if (tutorialCurrentStepId === 'solution-upload-requirement') {
                    // Explicitly disable runs on the requirement explanation step
                    runsEnabled = false;
                  } else if (tutorialCurrentStepId === 'solution-upload-select-run' ||
                             tutorialCurrentStepId?.includes('viewer')) {
                    // Enable runs on the select-run step and viewer steps
                    runsEnabled = true;
                  } else {
                    // For other tutorial steps, use default behavior
                    runsEnabled = activeProblemIsBadTask || hasSolution;
                  }
                } else {
                  // Outside tutorial: enable if read-only mode, bad task, or has solution
                  runsEnabled = isReadOnly || activeProblemIsBadTask || hasSolution;
                }

                return (
                  <button
                    key={run.id}
                    type="button"
                    className={`run-list-item ${statusClass} ${isSelected ? "selected" : ""} ${!runsEnabled ? "disabled-no-solution" : ""}`.trim()}
                    onClick={() => runsEnabled ? handleRunClick(run.id) : undefined}
                    disabled={!runsEnabled}
                    title={
                      !runsEnabled
                        ? "Upload a solution before selecting runs to label"
                        : statusClass === "labeled-success"
                        ? "Labeled as success"
                        : statusClass === "labeled-bad-task"
                        ? "Labeled as bad task"
                        : statusClass === "labeled-failed"
                        ? "Labeled as fail"
                        : "Pending your review"
                    }
                  >
                    <span className="run-icon" />
                    <span className="run-label">Run {idx + 1}</span>
                    <span className="run-status-text">
                      {statusClass === "labeled-success"
                        ? "✓ Labeled"
                        : statusClass === "labeled-bad-task"
                        ? "⚠ Bad Task"
                        : statusClass === "labeled-failed"
                        ? "✗ Labeled"
                        : "Needs label"}
                    </span>
                  </button>
                );
              })}

              {activeRuns.length === 0 && (
                <div className="run-empty-state">
                  <p>No runs are associated with this problem yet.</p>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="problem-empty-state">
            <h3>No runs available</h3>
            <p>Your assigned problems will appear in the list on the left.</p>
          </div>
        )}
      </section>
      {showBadTaskModal && (
        <div className="bad-task-modal-overlay" onClick={handleCloseBadTaskModal}>
          <div className="bad-task-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Mark Problem as Bad Task</h3>
            <p className="bad-task-modal-text">
              Provide a brief reason explaining why this problem is ambiguous or has multiple solutions. All run labels will be marked as completed once you flag this problem.
            </p>
            <textarea
              className="bad-task-modal-textarea"
              value={badTaskReasonInput}
              onChange={(e) => {
                setBadTaskReasonInput(e.target.value);
                if (badTaskError) {
                  setBadTaskError(null);
                }
              }}
              placeholder="Enter your reason here..."
              rows={4}
              autoFocus
            />
            {badTaskError && <div className="bad-task-modal-error">{badTaskError}</div>}
            <div className="bad-task-modal-actions">
              <button
                type="button"
                className="bad-task-modal-btn cancel"
                onClick={handleCloseBadTaskModal}
                disabled={submittingBadTask}
              >
                Cancel
              </button>
              <button
                type="button"
                className="bad-task-modal-btn confirm"
                onClick={handleConfirmBadTask}
                disabled={submittingBadTask}
              >
                {submittingBadTask ? "Saving..." : "Save Reason"}
              </button>
            </div>
          </div>
        </div>
      )}
      {showProjectTutorialOverlay && tutorial.state.currentStepId && (
        <OnboardingOverlayNew
          currentStepId={tutorial.state.currentStepId}
          onStepComplete={handleTutorialStepComplete}
          onStepPrevious={handleTutorialStepPrevious}
          onComplete={handleTutorialComplete}
          onNextTask={tutorial.nextTask}
          currentTaskIndex={tutorial.state.currentTaskIndex}
          software={tutorial.state.software}
          evaluationReady={false}
        />
      )}
      {activeProblem && (
        <PlaygroundModal
          isOpen={isPlaygroundModalOpen}
          onClose={() => setIsPlaygroundModalOpen(false)}
          problemId={activeProblem.id}
          software={activeProblem.software || ''}
          problemPrompt={activeProblem.prompt}
        />
      )}
      <MultimediaTaskHelpModal
        isOpen={showMultimediaHelpModal}
        onClose={() => setShowMultimediaHelpModal(false)}
      />
    </div>
  );
};

export default ProjectProblemWorkspace;
